import { isNil, isEmpty, get } from 'lodash';

/**
 * Gets mission id from mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} The mission id or an empty string if undefined.
 * @throws {Error} If mission is null or undefined.
 */
export const getId = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, '_id', '');
};

/**
 * Gets mission type from mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} The mission type or an empty string if undefined.
 * @throws {Error} If mission is null or undefined.
 */
export const getType = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'missionType', '');
};

/**
 * Checks if mission is superficial.
 *
 * @param {object} missionObject - The mission object.
 * @returns {boolean} Returns true if the mission is superficial and false otherwise.
 * @throws {Error} If mission is null or undefined.
 */
export const isSuperficial = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'missionType', false) === 'Surface Area';
};

/**
 * Checks if mission is lineal.
 *
 * @param {object} missionObject - The mission object.
 * @returns {boolean} Returns true if the mission is lineal and false otherwise.
 * @throws {Error} If mission is null or undefined.
 */
export const isLineal = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'missionType', false) === 'Linear Area';
};

/**
 * Looks for the mission geometry in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The mission geometry feature or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getGeometry = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  if (isSuperficial(missionObject)) {
    return get(missionObject, 'flightPlan.missionArea', {});
  }
  if (isLineal(missionObject)) {
    return get(missionObject, 'flightPlan.missionAxis', {});
  }
  return {};
};

/**
 * Looks for the waypoint list in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The waypoint feature collection or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getWaypointList = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.missionCalculation.waypointList', {});
};

/**
 * Looks for the waypoint list sent to the rpa in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {Array} The waypoint feature collection or an empty array if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getWaypointTelemetryArray = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.waypointTelemetryArray', []);
};

/**
 * Looks for the elevation profile in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The elevation profile feature collection or an empty object
 * if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getElevationProfile = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.elevationProfile', {});
};

/**
 * Looks for the flight parameters in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The flight parameters or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getFlightParameters = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.flightParameters', {});
};

/**
 * Looks for the take off point in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The take off feature point or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getTakeOffPoint = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.takeOffPoint', {});
};

/**
 * Looks for the landing point in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The landing point feature or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getLandingPoint = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.landingPoint', {});
};

/**
 * Gets the landing bearing from the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The landing bearing feature or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getLandingBearing = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.landingBearing', {});
};

/**
 * Gets the reference altitude that should be used to calculate the
 * waypoint altitude (AMSL) in this mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} Returns the reference altitude for the waypotins
 * 'DTM' for the waypoints to use AGL altitudes
 * 'TakeOffGPS' for waypoints to use the altitude relative to the waypoint.
 * Empty string if not found.
 * @throws {Error} If mission is null or undefined.
 */
export const getWaypointReferenceAltitude = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'waypointReferenceAltitude', '');
};

/**
 * Checks if the mission object waypoint reference altitude is DTM.
 *
 * @param {object} missionObject - The mission object.
 * @returns {boolean} Returns true if the waypoint reference altitude is DTM and false otherwise.
 * @throws {Error} If mission is null or undefined.
 */
export const isGroundDataDTM = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'waypointReferenceAltitude', false) === 'DTM';
};

/**
 * Checks if the mission object has waypoint reference altitude is Take Off GPS.
 *
 * @param {object} missionObject - The mission object.
 * @returns {boolean} Returns true if the waypoint reference altitude is Take Off GPS and false otherwise.
 * @throws {Error} If mission is null or undefined.
 */
export const isGroundDataTakeOffGPS = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'waypointReferenceAltitude', false) === 'TakeOffGPS';
};

/**
 * Looks for the picture grid parameters in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {object} The picture grid parameters or an empty object if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getPictureGridParams = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.pictureGrid', {});
};

/**
 * Looks for Axis Buffer in a lineal mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The axis buffer or undefined if doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getAxisBuffer = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.flightParameters.axisBuffer');
};

/**
 * Check if mission parameters are defined.
 *
 * @param {object} missionObject - The mission object.
 * @returns {boolean} Return true if all mission params exist, otherwiese returns false.
 * @throws {Error} If mission is null or undefined.
 */
export const areMissionParamsDefined = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  const flightParams = get(missionObject, 'flightPlan.flightParameters', {});
  const pictureGrid = get(missionObject, 'flightPlan.pictureGird', {});
  if (isEmpty(flightParams) || isEmpty(pictureGrid)) {
    return false;
  }
  return true;
};

/**
 * Looks for the mission height in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The mission height or undefined if doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getDesiredHeight = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.flightParameters.height');
};

/**
 * Looks for the speed in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The mission speed or undefined if doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getDesiredSpeed = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.flightParameters.speed');
};

/**
 * Looks for the overlap in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The overlap or undefined if doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getOverlap = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.pictureGrid.overlap');
};

/**
 * Looks for the sidelap in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The sidelap or undefined if doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getSidelap = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.pictureGrid.sidelap');
};

/**
 * Get the name for the mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} The name of the mission or an empty string if it does not exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getName = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'name', '');
};

/**
 * Looks for the distance between photos in the mission object.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number} The distance between each photo.
 * @throws {Error} If the waypoint list does not exist or if the mission is null or undefined.
 */
export const getShootDistance = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.missionCalculation.missionCalculatedData.distPics');
};

/**
 * Looks for the ground sample resolution for the mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The ground resolution for the mission or undefined if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getGroundSampleResolution = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.missionCalculation.missionCalculatedData.resolution');
};

/**
 * Looks for the theoric distance travelled by the rpa for the mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The distance travelled by the rpa for the mission or undefined if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getPathLength = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.missionCalculation.missionCalculatedData.pathLength');
};

/**
 * Looks for the total area to cover for the mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The total area for the mission or undefined if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getTotalArea = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.missionCalculation.missionCalculatedData.totalArea');
};

/**
 * Looks for the flight time in minutes for the mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The flight time in minutes for the mission or undefined if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getFlightTimeMinutes = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(
    missionObject,
    'flightPlan.missionCalculation.missionCalculatedData.flightTimeMinutes'
  );
};

/**
 * Looks for the shoot time between pictures.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The shoot time between pictures in seconds or undefined if it doesn't exist.
 * @throws {Error} If mission is null or undefined.
 */
export const getShootTime = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.missionCalculation.missionCalculatedData.shootTime');
};

/**
 * Looks for the entry margin in the mission.
 *
 * @param {object} missionObject - The mission object.
 * @returns {number|undefined} The entry margin for the mission or undefined if it's not defined.
 * @throws {Error} If mission is null or undefined.
 */
export const getEntryMargin = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'flightPlan.flightParameters.entryMargin');
};

/**
 * Get mission rpa id.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} The id of the rpa selected for the mission or an empty string if it's not defined.
 * @throws {Error} If mission is null or undefined.
 */
export const getRPA = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'rpa', '');
};

/**
 * Get mission payload id.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} The id of the payload selected for the mission or an empty string if it's not defined.
 * @throws {Error} If mission is null or undefined.
 */
export const getPayload = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'payload', '');
};

/**
 * Get mission description.
 *
 * @param {object} missionObject - The mission object.
 * @returns {string} The mission description or an emtpy string if undefined.
 * @throws {Error} If mission is null or undefined.
 */
export const getDescription = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'description', '');
};

/**
 * Checks if a mission is deleted or not.
 *
 * @param {object} missionObject - The mission object.
 * @returns {boolean} True if the mission is the trash or false otherwise.
 * @throws {Error} If mission is null or undefined.
 */
export const isDeleted = missionObject => {
  if (isNil(missionObject)) {
    throw new Error('Mission not defined');
  }
  return get(missionObject, 'deleted', false) !== 'no';
};
